import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon/Icon';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { SetChip, SetInput, SetSelect, SetSwitch, SetTextarea } from '../SettingsShell/controls';
import type { Closure, GeneralInfoData } from '../../settings-practice/types';

export interface GeneralInfoViewProps {
  data: GeneralInfoData;
  location: string;
  /** Online Booking owns the booking address once it's on — the field goes
   * read-only with a Copy, so the two can't drift (spec 50 §2.2). */
  bookingLocked?: boolean;
  /** Called with the whole page after every change (the page saves immediately). */
  onChange?: (next: GeneralInfoData) => void;
}

const TIMEZONES = [
  'Central Time (US & Canada)',
  'Eastern Time (US & Canada)',
  'Mountain Time (US & Canada)',
  'Pacific Time (US & Canada)',
].map((t) => ({ value: t, label: t }));

/** Practice management › General info (spec 15 §4). Six cards, immediate save. */
export function GeneralInfoView({ data, location, bookingLocked = false, onChange }: GeneralInfoViewProps) {
  const [g, setG] = useState(data);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    onChangeRef.current?.(g);
  }, [g]);
  const set = <K extends keyof GeneralInfoData>(k: K, v: GeneralInfoData[K]) => {
    setG((prev) => ({ ...prev, [k]: v }));
  };

  const running = g.closures.find((c) => c.runningNow);
  const disclosureEmpty = g.callDisclosure.trim() === '';

  return (
    <div>
      <SettingsPageHeader
        eyebrow="Practice management"
        titleLead="General"
        titleAccent="settings"
        sub={<>These settings are for this location only.</>}
      />

      <SettingsCards>
        {/* 01 — the practice */}
        <SettingsCard
          n="01"
          title="The practice"
          sub={
            <>
              ELVA uses the name in every greeting, message and signature. The rest fills the footer of every email.
            </>
          }
        >
          <SettingsRow
            label="Logo"
            desc={
              <>Shown in emails and on the pages patients open. PNG or SVG.</>
            }
            control={
              <span className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[var(--brand-indigo-tint)] text-[12px] font-bold text-[var(--brand-indigo)]">
                  {g.logoInitials}
                </span>
                <button type="button" className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
                  Replace →
                </button>
              </span>
            }
          />
          <SettingsRow
            label="Practice name"
            desc={
              <>ELVA says this name out loud, exactly as written.</>
            }
            control={
              <SetInput
                ariaLabel="Practice name"
                className="w-[300px]"
                value={g.practiceName}
                onChange={(v) => { set('practiceName', v); }}
              />
            }
          />
          <SettingsRow
            label="Tagline"
            desc="Shown under the name in emails. Optional."
            control={
              <SetInput
                ariaLabel="Tagline"
                className="w-[300px]"
                value={g.tagline}
                onChange={(v) => { set('tagline', v); }}
              />
            }
          />
          <SettingsRow
            label="Address"
            desc="The email footer, and what ELVA reads when a patient asks where you are."
            control={
              <SetInput
                ariaLabel="Address"
                className="w-[360px]"
                value={g.address}
                onChange={(v) => { set('address', v); }}
              />
            }
          />
          <SettingsRow
            label="Phone patients should use"
            desc={
              <>
                Printed in emails and given out when a patient asks. The number ELVA calls from is on Phone numbers.
              </>
            }
            control={
              <SetInput
                ariaLabel="Phone patients should use"
                className="w-[220px]"
                value={g.phone}
                onChange={(v) => { set('phone', v); }}
              />
            }
          />
          <SettingsRow
            label="Support email"
            desc="Shown in the email footer for replies."
            control={
              <SetInput
                ariaLabel="Support email"
                className="w-[300px]"
                value={g.supportEmail}
                onChange={(v) => { set('supportEmail', v); }}
              />
            }
          />
          <SettingsRow
            label="Website"
            desc="The email footer, and offered when a patient asks for it."
            control={
              <SetInput
                ariaLabel="Website"
                className="w-[300px]"
                value={g.website}
                onChange={(v) => { set('website', v); }}
              />
            }
          />
          <SettingsRow
            last
            label="Time zone"
            desc={
              <>All hours and send times use this time zone.</>
            }
            control={
              <SetSelect
                ariaLabel="Time zone"
                value={g.timezone}
                options={TIMEZONES}
                onChange={(v) => { set('timezone', v); }}
              />
            }
          />
        </SettingsCard>

        {/* 02 — production goals */}
        <SettingsCard
          n="02"
          title="Production goals"
          sub={<>Calendar insights compares production with these goals.</>}
        >
          <SettingsRow
            label="This week"
            desc="Production, not collections."
            control={
              <SetInput
                ariaLabel="Weekly production goal"
                prefix="$"
                mono
                className="w-[160px]"
                value={g.goalWeek}
                onChange={(v) => { set('goalWeek', v); }}
              />
            }
          />
          <SettingsRow
            last
            label="This month"
            desc={
              <>Each provider&rsquo;s monthly target is on Providers.</>
            }
            control={
              <SetInput
                ariaLabel="Monthly production goal"
                prefix="$"
                mono
                className="w-[160px]"
                value={g.goalMonth}
                onChange={(v) => { set('goalMonth', v); }}
              />
            }
          />
        </SettingsCard>

        {/* 03 — business hours */}
        <SettingsCard
          n="03"
          title="Business hours"
          sub={
            <>
              When the practice is open. The after-hours greeting and emergency routing follow these hours.
            </>
          }
        >
          {g.hours.map((d) => (
            <SettingsRow
              key={d.label}
              muted={!d.open}
              label={d.label}
              control={
                <span className="flex items-center gap-3">
                  <SetSwitch
                    label={`${d.label} open`}
                    checked={d.open}
                    onChange={(next) => {
                      set(
                        'hours',
                        g.hours.map((h) => (h.label === d.label ? { ...h, open: next } : h)),
                      );
                    }}
                  />
                  {d.open ? (
                    <>
                      <SetInput ariaLabel={`${d.label} opens`} className="w-[110px]" value={d.from} />
                      <span className="text-[12.5px] text-[var(--text-muted)]">to</span>
                      <SetInput ariaLabel={`${d.label} closes`} className="w-[110px]" value={d.to} />
                    </>
                  ) : (
                    <span className="text-[13px] text-[var(--text-muted)]">Closed</span>
                  )}
                </span>
              }
            />
          ))}
          <SettingsNote>
            <span className="flex items-start gap-2">
              <Icon kind="clock" size={13} stroke={2} style={{ marginTop: 2 }} />
              <span>Holidays and one-off closures go in the next card. Quiet hours are set per channel.</span>
            </span>
          </SettingsNote>
        </SettingsCard>

        {/* 04 — closures */}
        <SettingsCard
          n="04"
          accent="amber"
          title="Closures"
          sub={<>Every day the practice is shut.</>}
        >
          {running ? (
            <div className="mx-6 mt-5 flex items-start gap-2.5 rounded-[10px] bg-[#FBF3E4] px-4 py-3 text-[12.5px] leading-[1.6] text-[var(--warn-amber)]">
              <Icon kind="alert" size={14} stroke={2} style={{ marginTop: 2 }} />
              <span>
                <strong>A closure is running until {running.end ?? 'further notice'}.</strong> Patients hear the holiday
                script, and ELVA offers no times on those dates.
              </span>
            </div>
          ) : null}

          {g.closures.map((c, i) => (
            <ClosureRow
              key={c.id}
              c={c}
              last={i === g.closures.length - 1}
              onDelete={() => {
                set(
                  'closures',
                  g.closures.filter((x) => x.id !== c.id),
                );
              }}
              onScript={(v) => {
                set(
                  'closures',
                  g.closures.map((x) => (x.id === c.id ? { ...x, script: v } : x)),
                );
              }}
            />
          ))}

          <div className="px-6 py-4">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-dashed border-[var(--border-default)] px-3 py-2 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--surface-sunken)]"
            >
              <Icon kind="plus" size={12} stroke={2.4} /> Add a closure
            </button>
          </div>

          <SettingsNote>A provider&rsquo;s time off is set on Providers.</SettingsNote>
        </SettingsCard>

        {/* 05 — call disclosure */}
        <SettingsCard
          n="05"
          accent="amber"
          title="Call disclosure"
          sub="Required before ELVA can place calls."
        >
          {disclosureEmpty ? (
            <div className="mx-6 mt-5 flex items-start gap-2.5 rounded-[10px] bg-[#FBF3E4] px-4 py-3 text-[12.5px] leading-[1.6] text-[var(--warn-amber)]">
              <Icon kind="alert" size={14} stroke={2} style={{ marginTop: 2 }} />
              <span>
                <strong>This field cannot be empty.</strong> A location with no disclosure text{' '}
                <strong>cannot place outbound calls</strong> — the capability goes unavailable and every agent that would
                have called says why. It is the only field in the product whose absence switches something off.
              </span>
            </div>
          ) : null}
          <SettingsRow
            last
            label="What ELVA reads at the start of every outbound call"
            desc={
              <>ELVA reads this exactly as written at the start of a call, and saves it with the call record.</>
            }
          >
            <div className="mt-3">
              <SetTextarea
                ariaLabel="Call disclosure"
                rows={2}
                value={g.callDisclosure}
                onChange={(v) => { set('callDisclosure', v); }}
              />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                <span className="text-[11px] text-[var(--text-muted)]">{g.disclosureVersion}</span>
                <button type="button" className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
                  Restore ELVA&rsquo;s template
                </button>
              </div>
            </div>
          </SettingsRow>
        </SettingsCard>

        {/* 06 — links */}
        <SettingsCard n="06" title="Links this location owns">
          <SettingsRow
            label="Google review link"
            desc={
              <>The link ELVA puts in review requests.</>
            }
            control={
              <SetInput
                ariaLabel="Google review link"
                className="w-[300px]"
                value={g.reviewLink}
                onChange={(v) => { set('reviewLink', v); }}
              />
            }
          />
          <SettingsRow
            last
            label="Booking link"
            desc={
              bookingLocked ? (
                <>Online Booking owns this address. ELVA offers it in texts, emails and on calls.</>
              ) : (
                <>ELVA offers this link in texts, emails and on calls.</>
              )
            }
            control={
              bookingLocked ? (
                <span className="flex items-center gap-2">
                  <code className="rounded-[8px] bg-[var(--surface-sunken)] px-2.5 py-2 font-[var(--font-mono)] text-[12.5px] text-[var(--text-secondary)]">
                    {g.bookingLink}
                  </code>
                  <button
                    type="button"
                    aria-label="Copy booking link"
                    onClick={() => { void navigator.clipboard.writeText(`https://${g.bookingLink}`); }}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
                  >
                    <Icon kind="copy" size={14} stroke={1.9} />
                  </button>
                </span>
              ) : (
                <SetInput
                  ariaLabel="Booking link"
                  className="w-[300px]"
                  value={g.bookingLink}
                  onChange={(v) => { set('bookingLink', v); }}
                />
              )
            }
          />
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter left="Changes save immediately and apply to live calls." location={location} />
    </div>
  );
}

function ClosureRow({
  c,
  last,
  onDelete,
  onScript,
}: {
  c: Closure;
  last: boolean;
  onDelete: () => void;
  onScript: (v: string) => void;
}) {
  return (
    <div className="px-6 py-[18px]" style={last ? undefined : { borderBottom: '1px solid #edf0f6' }}>
      <div className="flex flex-wrap items-center gap-3">
        <SetInput ariaLabel="Closure reason" className="w-[200px]" value={c.reason} />
        <span className="text-[12.5px] text-[var(--text-muted)]">from</span>
        <SetInput ariaLabel="Closure start" className="w-[150px]" value={c.start} />
        <span className="text-[12.5px] text-[var(--text-muted)]">to</span>
        <SetInput ariaLabel="Closure end" className="w-[150px]" value={c.end ?? ''} />
        <span className="ml-auto flex items-center gap-2">
          {c.runningNow ? <SetChip tone="amber">RUNNING NOW</SetChip> : null}
          <button
            type="button"
            aria-label={`Delete ${c.reason}`}
            onClick={onDelete}
            className="flex h-8 w-8 items-center justify-center rounded-[9px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
          >
            <Icon kind="trash" size={13} stroke={2} />
          </button>
        </span>
      </div>

      {c.runningNow ? (
        <div className="mt-4">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            What ELVA says instead of the usual greeting
          </div>
          <SetTextarea
            ariaLabel="Closure script"
            rows={2}
            value={c.script ?? ''}
            onChange={onScript}
          />
          <p className="m-0 mt-2 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
            Type{' '}
            <code className="rounded bg-[var(--surface-sunken)] px-1 py-0.5 font-[var(--font-mono)] text-[11px] text-[var(--brand-indigo)]">
              {'{reopens}'}
            </code>{' '}
            to insert the reopening date. Left empty, ELVA says the practice is closed and when it reopens.
          </p>
        </div>
      ) : null}
    </div>
  );
}
